import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { propertyAdEditorProvider } from '../../providers/propertyAdEditorProvider';
import type { AdMediaType } from '../../types/ads';
import { FileKindEnum } from '../../types/enums';
import type { PropertyAdInProgressWorkspace } from '../../types/propertyAdEditor';

type MediaWorkspace = Pick<PropertyAdInProgressWorkspace, 'id' | 'propertyAdMedia'>;

export interface PropertyAdEditorMediaIdleResult {
  media: AdMediaType[];
  ok: boolean;
}

export const usePropertyAdEditorMedia = (workspace: MediaWorkspace) => {
  const [media, setMedia] = useState(workspace.propertyAdMedia ?? []);
  const mediaRef = useRef(media);
  const updateQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingUpdatesRef = useRef(0);
  const pendingUploadsRef = useRef(0);
  const uploadWaitersRef = useRef<Array<() => void>>([]);
  const failedKindsRef = useRef(new Set<FileKindEnum>());

  useEffect(() => {
    const nextMedia = workspace.propertyAdMedia ?? [];
    mediaRef.current = nextMedia;
    setMedia(nextMedia);
  }, [workspace.id, workspace.propertyAdMedia]);

  const beginUpload = useCallback(() => {
    pendingUploadsRef.current += 1;
  }, []);

  const finishUpload = useCallback((kind: FileKindEnum, ok: boolean) => {
    pendingUploadsRef.current -= 1;
    if (ok) failedKindsRef.current.delete(kind);
    else failedKindsRef.current.add(kind);

    if (pendingUploadsRef.current === 0) {
      uploadWaitersRef.current.splice(0).forEach((resolve) => resolve());
    }
  }, []);

  const persistMediaKind = useCallback(
    (kind: FileKindEnum, nextKindMedia: AdMediaType[]) => {
      const runUpdate = async () => {
        const nextMediaWithUrls = [
          ...mediaRef.current.filter((item) => item.kind !== kind),
          ...nextKindMedia,
        ];
        const requestMedia = nextMediaWithUrls.map(({ fileId, kind, mediaType, order }) => ({
          fileId,
          kind,
          ...(mediaType !== undefined ? { mediaType } : {}),
          order,
        }));
        let response: Awaited<ReturnType<typeof propertyAdEditorProvider.updateInProgressMedia>>;
        try {
          response = await propertyAdEditorProvider.updateInProgressMedia(workspace.id, {
            propertyAdMedia: requestMedia,
          });
        } catch (error) {
          failedKindsRef.current.add(kind);
          throw error;
        }

        if (response.ok) {
          const mediaUrlByFileId = new Map(
            nextMediaWithUrls
              .filter(({ fileId }) => Boolean(fileId))
              .map((item) => [item.fileId, item.fileUrl] as const)
          );
          const responseMedia = response.data?.propertyAdMedia;
          const confirmedMedia = Array.isArray(responseMedia) ? responseMedia : nextMediaWithUrls;
          const nextMedia = confirmedMedia.map((item) => ({
            ...item,
            fileUrl: item.fileUrl ?? mediaUrlByFileId.get(item.fileId),
          }));

          mediaRef.current = nextMedia;
          setMedia(nextMedia);
          failedKindsRef.current.delete(kind);
        } else {
          failedKindsRef.current.add(kind);
        }

        return response;
      };
      pendingUpdatesRef.current += 1;
      const queuedUpdate = updateQueueRef.current.then(runUpdate, runUpdate);

      updateQueueRef.current = queuedUpdate.then(
        () => {
          pendingUpdatesRef.current -= 1;
        },
        () => {
          pendingUpdatesRef.current -= 1;
        }
      );

      return queuedUpdate;
    },
    [workspace.id]
  );

  const persistPhotos = useCallback(
    (photos: AdMediaType[]) => persistMediaKind(FileKindEnum.Image, photos),
    [persistMediaKind]
  );
  const persistVideos = useCallback(
    (videos: AdMediaType[]) => persistMediaKind(FileKindEnum.Video, videos),
    [persistMediaKind]
  );
  const hasPendingMedia = useCallback(
    () => pendingUploadsRef.current > 0 || pendingUpdatesRef.current > 0,
    []
  );
  const waitForMediaIdle = useCallback(async (): Promise<PropertyAdEditorMediaIdleResult> => {
    for (;;) {
      if (pendingUploadsRef.current > 0) {
        await new Promise<void>((resolve) => uploadWaitersRef.current.push(resolve));
      }

      const queue = updateQueueRef.current;
      await queue;

      if (pendingUploadsRef.current === 0 && queue === updateQueueRef.current) {
        return { media: mediaRef.current, ok: failedKindsRef.current.size === 0 };
      }
    }
  }, []);
  const mediaWorkspace = useMemo(
    () => ({ id: workspace.id, propertyAdMedia: media }),
    [media, workspace.id]
  );

  return {
    beginUpload,
    finishUpload,
    hasPendingMedia,
    media,
    mediaWorkspace,
    persistPhotos,
    persistVideos,
    waitForMediaIdle,
  };
};
